/**
 * Lead detail (F3-I): editable header (stage/assignment), event timeline
 * with composer (note/call/visit), WhatsApp actions (logs a `whatsapp`
 * event when opening the chat) and a conversion-to-student dialog.
 */
import { createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Link } from "@tanstack/solid-router";
import { ArrowLeft, MessageCircle, UserCheck } from "@fleet/icons";
import { buildWaLink } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Label, Skeleton, TextField } from "@fleet/ui";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { showToast } from "@/components/ui/toast";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import { STAGES, useAddEvent, useConvertLead, useLead, usePatchLead, useTemplates } from "./api";
import { eventLabel, STAGE_LABEL } from "./labels";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

// `to` string (accepted by TanStack Link, like the shell's NAV) so the link
// isn't coupled to the generated routes union.
const CRM_PATH: string = "/crm";

export function LeadDetail(props: { leadId: string }) {
  const { t } = useI18n();
  const { activeOrgId } = useActiveOrg();
  const q = useLead(activeOrgId, () => props.leadId);
  const patch = usePatchLead();
  const addEvent = useAddEvent();
  const convert = useConvertLead();

  const [composerKind, setComposerKind] = createSignal<string>("note");
  const [composerBody, setComposerBody] = createSignal("");
  const [convertOpen, setConvertOpen] = createSignal(false);

  const lead = () => q.data?.lead;

  const setStage = (stage: string) => {
    patch.mutate(
      { id: props.leadId, patch: { stage } },
      { onError: (e) => showToast(e.message, "error") },
    );
  };

  const setAssigned = (value: string) => {
    patch.mutate(
      { id: props.leadId, patch: { assignedTo: value.trim() || null } },
      {
        onSuccess: () => showToast(t(msg`Saved`), "success"),
        onError: (e) => showToast(e.message, "error"),
      },
    );
  };

  const submitEvent = (e: Event) => {
    e.preventDefault();
    if (!composerBody().trim()) {
      return;
    }
    addEvent.mutate(
      { id: props.leadId, kind: composerKind(), body: composerBody().trim() },
      {
        onSuccess: () => {
          setComposerBody("");
          showToast(t(msg`Event added`), "success");
        },
        onError: (err) => showToast(err.message, "error"),
      },
    );
  };

  // AUTONOMY: the message comes from the chosen WhatsApp template (CRM →
  // Templates), interpolating {{nombre}}/{{curso}} — used to be fixed text.
  const templates = useTemplates(() => activeOrgId());
  const [waTemplateId, setWaTemplateId] = createSignal("");
  const waHref = (): string | null => {
    const l = lead();
    if (!l) {
      return null;
    }
    // An anonymized lead (its account was deleted) has neither name nor
    // phone: with an empty phone `buildWaLink` returns null and the
    // WhatsApp button never gets offered.
    const name = l.name ?? t(msg`Deleted account`);
    const selected = (templates.data ?? []).find((tpl) => tpl.id === waTemplateId());
    if (selected) {
      return buildWaLink(l.phone ?? "", selected.body, {
        nombre: l.name ?? "",
        curso: l.interest ?? "",
      });
    }
    return buildWaLink(l.phone ?? "", t(msg`Hi ${name}, this is Space Academy.`));
  };

  const openWhatsApp = () => {
    const href = waHref();
    if (!href) {
      return;
    }
    // Logs the WhatsApp contact in the timeline.
    addEvent.mutate({ id: props.leadId, kind: "whatsapp", body: t(msg`Contacted via WhatsApp`) });
    window.open(href, "_blank", "noopener");
  };

  return (
    <div>
      <Link
        to={CRM_PATH}
        class="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft class="h-4 w-4" /> {t(msg`Back to pipeline`)}
      </Link>

      <Show
        when={q.isLoading ? undefined : lead()}
        fallback={<Skeleton class="h-64 w-full rounded-xl" />}
      >
        {(l) => (
          <div class="grid gap-6 lg:grid-cols-[1fr_1.4fr]">
            {/* Header + actions */}
            <div class="space-y-4 rounded-xl border border-border bg-card p-5">
              <div>
                <h2 class="text-2xl font-bold">{l().name ?? t(msg`Deleted account`)}</h2>
                <p class="text-muted-foreground">{l().phone}</p>
                <Show when={l().email}>
                  <p class="text-sm text-muted-foreground">{l().email}</p>
                </Show>
              </div>

              <div class="space-y-2">
                <Label class="text-xs font-bold uppercase tracking-wider opacity-50">
                  {t(msg`Stage`)}
                </Label>
                <Select
                  options={STAGES.map((s) => ({ value: s, label: t(STAGE_LABEL[s]) }))}
                  value={l().stage}
                  onChange={setStage}
                  aria-label={t(msg`Stage`)}
                />
              </div>

              <div class="space-y-2">
                <Label class="text-xs font-bold uppercase tracking-wider opacity-50">
                  {t(msg`Assigned to (user id)`)}
                </Label>
                <input
                  class={field}
                  value={l().assignedTo ?? ""}
                  onChange={(e) => setAssigned(e.currentTarget.value)}
                  placeholder={t(msg`Unassigned`)}
                />
              </div>

              {/* AUTONOMY: message template (CRM → Templates). */}
              <Show when={(templates.data ?? []).length > 0}>
                <div class="space-y-2">
                  <Label class="text-xs font-bold uppercase tracking-wider opacity-50">
                    {t(msg`WhatsApp template`)}
                  </Label>
                  <Select
                    aria-label={t(msg`WhatsApp template`)}
                    value={waTemplateId()}
                    onChange={setWaTemplateId}
                    options={[
                      { value: "", label: t(msg`Default message`) },
                      ...(templates.data ?? []).map((tpl) => ({ value: tpl.id, label: tpl.name })),
                    ]}
                    data-testid="lead-wa-template"
                  />
                </div>
              </Show>

              <div class="flex flex-wrap gap-2 pt-2">
                <Show when={waHref()}>
                  <Button
                    variant="outline"
                    class="gap-2"
                    onClick={openWhatsApp}
                    data-testid="lead-wa"
                  >
                    <MessageCircle class="h-4 w-4" /> {t(msg`WhatsApp`)}
                  </Button>
                </Show>
                <Show
                  when={!l().convertedUserId}
                  fallback={<span class="text-sm text-green-600">{t(msg`Enrolled ✓`)}</span>}
                >
                  <Button
                    class="gap-2"
                    onClick={() => setConvertOpen(true)}
                    data-testid="lead-convert"
                  >
                    <UserCheck class="h-4 w-4" /> {t(msg`Convert to student`)}
                  </Button>
                </Show>
              </div>
            </div>

            {/* Timeline + composer */}
            <div class="space-y-4">
              <form
                onSubmit={submitEvent}
                class="rounded-xl border border-border bg-card p-4"
                data-testid="lead-composer"
              >
                <div class="mb-2 flex gap-2">
                  <div class="w-40">
                    <Select
                      options={["note", "call", "visit", "whatsapp", "email"].map((k) => ({
                        value: k,
                        label: t(eventLabel(k)),
                      }))}
                      value={composerKind()}
                      onChange={setComposerKind}
                      aria-label={t(msg`Event type`)}
                    />
                  </div>
                </div>
                <textarea
                  class={field}
                  rows={2}
                  value={composerBody()}
                  onInput={(e) => setComposerBody(e.currentTarget.value)}
                  placeholder={t(msg`Log a note, call or visit…`)}
                />
                <div class="mt-2 flex justify-end">
                  <Button type="submit" disabled={addEvent.isPending || !composerBody().trim()}>
                    {t(msg`Add to timeline`)}
                  </Button>
                </div>
              </form>

              <div class="space-y-2" data-testid="lead-timeline">
                <For each={q.data?.events ?? []}>
                  {(ev) => (
                    <div class="rounded-lg border border-border bg-card p-3">
                      <div class="flex items-center justify-between">
                        <span class="text-xs font-bold uppercase tracking-wide text-accent">
                          {t(eventLabel(ev.kind))}
                        </span>
                        <span class="text-xs text-muted-foreground">
                          {new Date(ev.createdAt).toLocaleString()}
                        </span>
                      </div>
                      <Show when={ev.body}>
                        <p class="mt-1 text-sm">{ev.body}</p>
                      </Show>
                    </div>
                  )}
                </For>
              </div>
            </div>
          </div>
        )}
      </Show>

      <ConvertDialog
        open={convertOpen()}
        onOpenChange={setConvertOpen}
        onConfirm={(input) =>
          convert.mutate(
            { id: props.leadId, input },
            {
              onSuccess: () => {
                showToast(t(msg`Lead converted to student`), "success");
                setConvertOpen(false);
              },
              onError: (err) => showToast(err.message, "error"),
            },
          )
        }
        pending={convert.isPending}
        defaults={{
          name: lead()?.name ?? "",
          email: lead()?.email ?? "",
          phone: lead()?.phone ?? "",
          cedula: lead()?.cedula ?? "",
        }}
      />
    </div>
  );
}

interface ConvertInput {
  userId?: string;
  createUser?: { name: string; email: string; phone?: string; cedula?: string; campusId?: string };
}

function ConvertDialog(props: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onConfirm: (input: ConvertInput) => void;
  pending: boolean;
  defaults: { name: string; email: string; phone: string; cedula: string };
}) {
  const { t } = useI18n();
  const [mode, setMode] = createSignal<"create" | "link">("create");
  const [name, setName] = createSignal(props.defaults.name);
  const [email, setEmail] = createSignal(props.defaults.email);
  const [phone, setPhone] = createSignal(props.defaults.phone);
  const [cedula, setCedula] = createSignal(props.defaults.cedula);
  const [userId, setUserId] = createSignal("");

  const confirm = () => {
    if (mode() === "link") {
      if (!userId().trim()) {
        showToast(t(msg`Enter the user id to link`), "error");
        return;
      }
      props.onConfirm({ userId: userId().trim() });
    } else {
      if (!name().trim() || !email().trim()) {
        showToast(t(msg`Name and email are required`), "error");
        return;
      }
      props.onConfirm({
        createUser: {
          name: name().trim(),
          email: email().trim(),
          phone: phone().trim() || undefined,
          cedula: cedula().trim() || undefined,
        },
      });
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} contentClass="max-w-md p-0">
      <DialogHeader size="md">
        <DialogTitle class="text-xl font-bold">{t(msg`Convert to student`)}</DialogTitle>
      </DialogHeader>
      <div class="space-y-4 p-6" data-testid="lead-convert-dialog">
        <div class="w-full">
          <Select
            options={[
              { value: "create", label: t(msg`Create a new user`) },
              { value: "link", label: t(msg`Link an existing user`) },
            ]}
            value={mode()}
            onChange={(v) => setMode(v === "link" ? "link" : "create")}
            aria-label={t(msg`Conversion mode`)}
          />
        </div>

        <Show
          when={mode() === "create"}
          fallback={
            <div class="space-y-2">
              <Label class="text-xs font-bold uppercase tracking-wider opacity-50">
                {t(msg`User id`)}
              </Label>
              <TextField
                value={userId()}
                onInput={(e) => setUserId(e.currentTarget.value)}
                placeholder={t(msg`Existing user id`)}
              />
            </div>
          }
        >
          <div class="space-y-3">
            <div class="space-y-2">
              <Label class="text-xs font-bold uppercase tracking-wider opacity-50">
                {t(msg`Name`)}
              </Label>
              <TextField value={name()} onInput={(e) => setName(e.currentTarget.value)} />
            </div>
            <div class="space-y-2">
              <Label class="text-xs font-bold uppercase tracking-wider opacity-50">
                {t(msg`Email`)}
              </Label>
              <TextField
                value={email()}
                onInput={(e) => setEmail(e.currentTarget.value)}
                type="email"
              />
            </div>
            <div class="grid grid-cols-2 gap-3">
              <div class="space-y-2">
                <Label class="text-xs font-bold uppercase tracking-wider opacity-50">
                  {t(msg`Phone`)}
                </Label>
                <TextField
                  value={phone()}
                  onInput={(e) => setPhone(e.currentTarget.value)}
                  type="tel"
                />
              </div>
              <div class="space-y-2">
                <Label class="text-xs font-bold uppercase tracking-wider opacity-50">
                  {t(msg`National ID`)}
                </Label>
                <TextField value={cedula()} onInput={(e) => setCedula(e.currentTarget.value)} />
              </div>
            </div>
          </div>
        </Show>

        <div class="flex items-center justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="ghost"
            class="h-11 rounded-xl px-6"
            onClick={() => props.onOpenChange(false)}
          >
            {t(msg`Cancel`)}
          </Button>
          <Button
            onClick={confirm}
            disabled={props.pending}
            data-testid="lead-convert-confirm"
            class="h-11 rounded-xl px-8 font-bold"
          >
            {t(msg`Convert`)}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
